import { Dialog } from '@headlessui/react';
import React from 'react';
import { X, Bell, CheckCircle, AlertTriangle, Info } from 'lucide-react';
import { clsx } from 'clsx';
import { useProperty, usePricing } from '../app/providers';

interface NotificationsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const NotificationsPanel: React.FC<NotificationsPanelProps> = ({
  isOpen,
  onClose,
}) => {
  const { selectedProperty } = useProperty();
  const { alerts, markAlertRead } = usePricing();

  const propertyAlerts = selectedProperty
    ? alerts.filter((alert) => alert.propertyId === selectedProperty.id)
    : [];

  const unreadAlerts = propertyAlerts.filter((a) => !a.read);
  const readAlerts = propertyAlerts.filter((a) => a.read).slice(0, 10);

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'warning':
      case 'error':
        return AlertTriangle;
      case 'success':
        return CheckCircle;
      default:
        return Info;
    }
  };

  const getAlertStyles = (type: string, read: boolean) => {
    const baseStyles = read ? 'opacity-60' : '';
    switch (type) {
      case 'warning':
        return clsx(
          'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200',
          baseStyles
        );
      case 'error':
        return clsx(
          'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-200',
          baseStyles
        );
      case 'success':
        return clsx(
          'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-200',
          baseStyles
        );
      default:
        return clsx(
          'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-200',
          baseStyles
        );
    }
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInMinutes = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60)
    );

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60)
      return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24)
      return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-[70]">
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-50 transition-opacity"
        onClick={onClose}
      />

      {/* Panel */}
      <Dialog.Panel className="fixed right-0 top-0 h-[100dvh] w-96 max-w-full bg-white dark:bg-slate-900 shadow-2xl z-50 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="shrink-0 bg-white dark:bg-slate-800 dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <Dialog.Title
              as="h2"
              className="text-lg font-semibold text-gray-900 dark:text-slate-100"
            >
              Notifications
            </Dialog.Title>
            {unreadAlerts.length > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded-full">
                {unreadAlerts.length} new
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close notifications"
            className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 dark:hover:bg-slate-800 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500 dark:text-slate-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 pb-[max(1rem,env(safe-area-inset-bottom))] space-y-3">
          {propertyAlerts.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="mx-auto h-12 w-12 text-gray-300 dark:text-slate-600 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-2">
                All caught up!
              </h3>
              <p className="text-gray-500 dark:text-slate-400">
                No notifications at the moment.
              </p>
            </div>
          ) : (
            <>
              {/* Unread alerts */}
              {unreadAlerts.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    New
                  </h3>
                  {unreadAlerts.map((alert) => {
                    const Icon = getAlertIcon(alert.type);
                    return (
                      <div
                        key={alert.id}
                        className={clsx(
                          'p-4 rounded-xl border-2 transition-all',
                          getAlertStyles(alert.type, false)
                        )}
                      >
                        <div className="flex items-start gap-3">
                          <Icon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-start justify-between gap-2">
                              <h4 className="font-semibold text-sm break-words min-w-0 max-w-full">
                                {alert.title}
                              </h4>
                              <span className="text-xs opacity-70 whitespace-nowrap">
                                {formatTimeAgo(alert.time)}
                              </span>
                            </div>
                            <p className="text-sm mt-1 opacity-90 break-words">
                              {alert.message}
                            </p>
                            <button
                              onClick={() => markAlertRead(alert.id)}
                              className="mt-2 text-xs font-medium hover:underline"
                            >
                              Mark as read
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Read alerts */}
              {readAlerts.length > 0 && (
                <div className="space-y-3 mt-6">
                  <h3 className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    Earlier
                  </h3>
                  {readAlerts.map((alert) => {
                    const Icon = getAlertIcon(alert.type);
                    return (
                      <div
                        key={alert.id}
                        className={clsx(
                          'p-3 rounded-xl border transition-all',
                          getAlertStyles(alert.type, true)
                        )}
                      >
                        <div className="flex items-start gap-3">
                          <Icon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-start justify-between gap-2">
                              <h4 className="font-medium text-sm break-words min-w-0 max-w-full">
                                {alert.title}
                              </h4>
                              <span className="text-xs opacity-70 whitespace-nowrap">
                                {formatTimeAgo(alert.time)}
                              </span>
                            </div>
                            <p className="text-xs mt-1 opacity-75 line-clamp-2">
                              {alert.message}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </Dialog.Panel>
    </Dialog>
  );
};

export default NotificationsPanel;
